import { Alert, Box } from '@mui/material';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { embedDashboard, EmbeddedDashboard } from '@superset-ui/embedded-sdk';
import { api } from 'services/api';
import { Loader } from 'ui/Loader';
import { MIN_EMBED_HEIGHT, SCROLL_SIZE_POLL_INTERVAL } from './constants';
import { TSupersetDashboardEmbedProps } from './types';

export const SupersetDashboardEmbed: FC<TSupersetDashboardEmbedProps> = ({
	supersetDomain,
	dashboardId,
	guestTokenUrl,
}) => {
	const mountRef = useRef<HTMLDivElement | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [height, setHeight] = useState(MIN_EMBED_HEIGHT);

	// The embedded SDK owns the token lifecycle and calls this again whenever the guest token
	// expires, so it has to stay a plain promise instead of going through a saga.
	const fetchGuestToken = useCallback(async (): Promise<string> => {
		const { token } = await api.superset.getSupersetTokenByUrl(guestTokenUrl, { dashboardId });

		if (!token) {
			throw new Error('Backend returned an empty guest token.');
		}

		return token;
	}, [dashboardId, guestTokenUrl]);

	useEffect(() => {
		let cancelled = false;
		let dashboard: EmbeddedDashboard | null = null;
		let pollId: ReturnType<typeof setInterval> | null = null;
		const mountPoint = mountRef.current;

		// The dashboard lives in a cross-origin iframe, so its content height can only be read
		// through the SDK, and it keeps changing as charts render, tabs switch or filters expand.
		// Polling and growing the iframe to the reported height keeps the scrollbar on the page
		// instead of nesting one inside the iframe.
		const syncHeight = async () => {
			if (!dashboard) return;

			try {
				const { height: scrollHeight } = await dashboard.getScrollSize();
				if (cancelled || !scrollHeight) return;
				setHeight((current) =>
					Math.abs(current - scrollHeight) > 1 ? Math.max(scrollHeight, MIN_EMBED_HEIGHT) : current,
				);
			} catch {
				// The iframe is not ready (or is already gone) — the next tick will pick it up.
			}
		};

		const mountDashboard = async () => {
			if (!mountPoint) return;

			setLoading(true);
			setError(null);
			mountPoint.innerHTML = '';

			try {
				dashboard = await embedDashboard({
					id: dashboardId,
					supersetDomain,
					mountPoint,
					fetchGuestToken,
					iframeTitle: 'Superset dashboard',
					dashboardUiConfig: {
						hideTitle: true,
						hideTab: false,
						hideChartControls: false,
						filters: {
							expanded: false,
						},
						urlParams: {
							standalone: '1',
						},
					},
				});

				if (cancelled) {
					dashboard.unmount();
					return;
				}

				setLoading(false);
				void syncHeight();
				pollId = setInterval(syncHeight, SCROLL_SIZE_POLL_INTERVAL);
			} catch (e) {
				if (cancelled) return;
				setError(e instanceof Error ? e.message : 'Failed to embed the dashboard');
				setLoading(false);
			}
		};

		void mountDashboard();

		return () => {
			cancelled = true;
			if (pollId) clearInterval(pollId);
			dashboard?.unmount();
			if (mountPoint) mountPoint.innerHTML = '';
		};
	}, [dashboardId, fetchGuestToken, supersetDomain]);

	return (
		<>
			{loading && <Loader />}
			{error && (
				<Alert severity="error" sx={{ mb: 2 }}>
					{error}
				</Alert>
			)}

			<Box
				ref={mountRef}
				sx={{
					display: loading || error ? 'none' : 'block',
					width: '100%',
					minWidth: '1200px',
					height: `${height}px`,
					'& iframe': {
						display: 'block',
						width: '100%',
						height: '100%',
						border: 'none',
					},
				}}
			/>
		</>
	);
};
